import React, { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDriversList, assignSupportDriverManually } from "../redux/expressSlice";
import { AppState } from "../../config/redux/reducers";
import { Grid, Box, GridSize, List } from "@material-ui/core";
import { useStyles } from "./DriverAssignment.styles";
import AppConstants from "../../constants";
import { createPayloadToFetchDriverList, createPayloadToAssignSupportDriver } from "../../mocks/express/response.transforms";
import { DriversFooter, DriversHeader, DriversList, DriversSearchHeader } from "./DriverCommonComponent";
import { useDriverVehicleFilter } from "./useDriverVehicleFilter";
import { useDriverSearch } from "./useDriverSearch";

interface DriverAssignmentProps {
  consignment?: any;
  countryCode?: string;
}

const SupportDriver = (props: DriverAssignmentProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [selectedDrivers, setSelectedDrivers] = useState<any[]>([]);
  const [isDriverSelected, setIsDriverSelected] = useState(false);
  const { consignment, countryCode } = props;
  const { driversList } = useSelector((state: AppState) => state.expressDashboard);
  const searchBoxRef = useRef<HTMLInputElement>(null);

  const vehicleFilter = useDriverVehicleFilter(driversList);
  const driverSearch = useDriverSearch({
    driversList,
    filterByVehicle: vehicleFilter.filterByVehicle,
  });

  useEffect(() => {
    driverSearch.handleSearch(driverSearch.searchValue);
  }, [vehicleFilter.selectedVehicle, driversList,  driverSearch.handleSearch, driverSearch.searchValue]);

  useEffect(() => {
    setSelectedDrivers([]);
  }, [driversList]);

  const handleListItemClick = useCallback((event: any, driver: any) => {
    setSelectedDrivers((prevSelectedDrivers) => {
      const newSelectedDrivers = new Set(prevSelectedDrivers);
      if (newSelectedDrivers.has(driver)) {
        newSelectedDrivers.delete(driver);
      } else {
        newSelectedDrivers.add(driver);
      }
      setIsDriverSelected(newSelectedDrivers.size > 0);
      return Array.from(newSelectedDrivers);
    });
  }, []);

  const manuallyAssignDriver = useCallback(
    (selectedDriver: any, consignment: any) => {
      const payload = createPayloadToAssignSupportDriver(selectedDriver, consignment);
      dispatch(assignSupportDriverManually({ payload }));
    },
    [dispatch]
  );

  const handleAssignDriver = useCallback(() => {
    if (selectedDrivers?.length > 0) {
      const selectedIDs = selectedDrivers.map((driver) => driver.driverId);
      if (selectedIDs.length > 0) {
        manuallyAssignDriver(selectedIDs, consignment);
      }
    }
  }, [selectedDrivers, consignment, manuallyAssignDriver]);

  const handleCancel = useCallback(() => {
    driverSearch.resetSearch();
    setIsDriverSelected(false);
    setSelectedDrivers([]);
    vehicleFilter.resetVehicleFilter();
  }, [driverSearch, vehicleFilter]);

  const fetchDriversLists = useCallback(
    (consignment: any, countryCode: any) => {
      if (Object.keys(consignment).length) {
        const payload = createPayloadToFetchDriverList(countryCode, consignment.posInformation, consignment.metadata?.activity, true);
        dispatch(fetchDriversList({ payload }));
        setSelectedDrivers([]);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (consignment !== null) {
      fetchDriversLists(consignment, countryCode);
      setIsDriverSelected(false);
      vehicleFilter.resetVehicleFilter();
    }
  }, [fetchDriversLists, countryCode, consignment, vehicleFilter.resetVehicleFilter]);

  const headerColumns = useMemo(
    () => [
      { label: AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.DRIVER_NAME, xs: 4 as GridSize },
      { label: AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.VEHICLE, xs: 2 as GridSize, style: { textAlign: "center" as const } },
      { label: AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.STATUS, xs: 3 as GridSize, style: { textAlign: "center" as const } },
      {
        label: (
          <span>
            {AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.PROXIMITY}
            <br />
            {AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.TO_HUB}
          </span>
        ),
        xs: 2 as GridSize,
        style: { textAlign: "center" as const },
      },
      { label: AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.ORDERS, xs: 1 as GridSize, style: { textAlign: "right" as const } },
    ],
    []
  );

  return (
    <>
      <DriversSearchHeader
        consignment={consignment}
        handleSearchBoxTextChange={driverSearch.handleSearchBoxTextChange}
        searchValue={driverSearch.searchValue}
        searchBoxRef={searchBoxRef}
        classes={classes}
        fetchDriversLists={fetchDriversLists}
        countryCode={countryCode}
        title={AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.SUPPORT_DRIVER_LIST}
        {...vehicleFilter}
      />
      <Grid className="driverListContainer" container direction="column">
        <DriversHeader columns={headerColumns} className="itemContainer header" />
        <Box className={classes.driverList}>
          <List component="nav" aria-label="driversList" style={{ paddingTop: "0px" }}>
            <DriversList
              listToShow={driverSearch.listToShow}
              selectedDrivers={selectedDrivers}
              handleListItemClick={handleListItemClick}
              classes={classes}
              selectionType="checkbox"
            />
          </List>
        </Box>
        <DriversFooter
          isDriverSelected={isDriverSelected}
          handleAssignDriver={handleAssignDriver}
          handleCancel={handleCancel}
          isAssignDisabled={!isDriverSelected}
        />
      </Grid>
    </>
  );
};

export default React.memo(SupportDriver);